import type { AnchorHTMLAttributes, ButtonHTMLAttributes, ReactNode } from 'react'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface CommonProps {
  variant?: ButtonVariant
  size?: ButtonSize
  block?: boolean
  /** Keeps the label and the width while showing a spinner. Swapping the text for
   *  a lone spinner makes the row jump and loses the one piece of context — what
   *  is happening — at exactly the moment somebody is wondering. */
  busy?: boolean
  children?: ReactNode
}

function classes({ variant = 'primary', size = 'md', block, className }: CommonProps & { className?: string }) {
  return [
    'mx-btn',
    `mx-btn-${variant}`,
    size !== 'md' && `mx-btn-${size}`,
    block && 'mx-btn-block',
    className,
  ]
    .filter(Boolean)
    .join(' ')
}

export function Button({
  variant,
  size,
  block,
  busy,
  children,
  className,
  disabled,
  type = 'button',
  ...rest
}: CommonProps & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type={type}
      className={classes({ variant, size, block, className })}
      disabled={disabled || busy}
      {...rest}
    >
      {busy && <span className="mx-spinner" aria-hidden="true" />}
      {children}
    </button>
  )
}

/** For navigation that happens to look like a button. A real `<a>`, so it opens in
 *  a new tab on a middle click like every other link on the page. */
export function ButtonLink({
  variant,
  size,
  block,
  children,
  className,
  ...rest
}: CommonProps & AnchorHTMLAttributes<HTMLAnchorElement>) {
  return (
    <a className={classes({ variant, size, block, className })} {...rest}>
      {children}
    </a>
  )
}

export function IconButton({
  label,
  children,
  variant = 'ghost',
  className,
  ...rest
}: { label: string } & CommonProps & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={[classes({ variant, className }), 'mx-btn-icon'].join(' ')}
      {...rest}
    >
      {children}
    </button>
  )
}

export function Spinner({ label = 'Loading' }: { label?: string }) {
  return (
    <div
      role="status"
      aria-label={label}
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '40vh',
        color: 'var(--muted)',
      }}
    >
      <span className="mx-spinner" style={{ width: 22, height: 22, borderWidth: 2.5 }} />
    </div>
  )
}
